/**
 * Artifact Component
 *
 * Generic presentation component for embedding interactive artifacts in chat.
 * Used for questionnaires, insights, and other embedded content types.
 *
 * Features:
 * - Configurable title, description, subtitle, and actions
 * - State-based styling via data-state attribute
 * - Separate click handlers for artifact body and button
 * - Optional icon with tilting animation (insight artifacts)
 * - Optional preview content mode for document previews (insights)
 * - Minimal mode for questionnaires (no icon, chevron-only actions)
 */

import { escapeHtml } from '../core/utils.js';

// Height crossfade for state swaps that change the card's size (see
// update({ animateHeight })). Short enough to finish before anything that
// follows it — the questionnaire settle scroll starts at 180ms.
const RESIZE_DURATION_MS = 150;

export class Artifact {
  /**
   * @param {Object} options - Configuration options
   * @param {string} options.iconId - SVG sprite icon ID (e.g., 'icon-search', 'icon-lightbulb')
   * @param {string} options.title - Primary text (top line)
   * @param {string} options.description - Description text (between title and subtitle)
   * @param {string} options.subtitle - Secondary text (bottom line, metadata)
   * @param {string} options.buttonText - Text for action button
   * @param {boolean} options.showButton - Whether to show the action button (default: true)
   * @param {boolean} options.showChevron - Whether to show standalone chevron when no button (default: false)
   * @param {string} options.chevronText - Optional text next to standalone chevron (e.g., 'Resume')
   * @param {boolean} options.showCheckmark - Whether to show checkmark icon (for completed state)
   * @param {string} options.state - Current state (e.g., 'ready', 'open', 'paused', 'completed')
   * @param {string} options.type - Artifact type for CSS class (e.g., 'questionnaire', 'insight')
   * @param {boolean} options.clickable - Whether the artifact is clickable (default: true)
   * @param {boolean} options.showIcon - Whether to show the icon area (default: true)
   * @param {Function} options.onArtifactClick - Handler for clicking the artifact body
   * @param {Function} options.onButtonClick - Handler for clicking the button
   * @param {Object} options.previewContent - Optional preview content for icon area (replaces icon)
   * @param {string} options.previewContent.title - Preview title text
   * @param {string} options.previewContent.body - Preview body text
   * @param {boolean} options.showNewTag - Whether to show NEW tag next to title (default: false)
   */
  constructor(options) {
    this.iconId = options.iconId || 'icon-document';
    this.title = options.title || '';
    this.description = options.description || '';
    this.subtitle = options.subtitle || '';
    this.buttonText = options.buttonText || 'Open';
    this.showButton = options.showButton !== false;
    this.showChevron = options.showChevron || false;
    this.chevronText = options.chevronText || '';
    this.showCheckmark = options.showCheckmark || false;
    this.showNewTag = options.showNewTag || false;
    this.showIcon = options.showIcon !== false;
    this.state = options.state || 'ready';
    this.type = options.type || null;
    this.clickable = options.clickable !== false;
    this.onArtifactClick = options.onArtifactClick || null;
    this.onButtonClick = options.onButtonClick || null;
    this.previewContent = options.previewContent || null;

    this.element = null;
    this._currentButton = null;

    // Bind handler for proper cleanup
    this._boundButtonClickHandler = this._handleButtonClick.bind(this);
  }

  /**
   * Render the artifact element
   * @returns {HTMLElement} The artifact element
   */
  render() {
    this.element = document.createElement('div');
    this.element.className = 'artifact';
    this.element.setAttribute('role', 'button');
    this.element.setAttribute('tabindex', '0');

    if (this.clickable) {
      this.element.classList.add('artifact--clickable');
    }

    if (this.type) {
      this.element.classList.add(`artifact--${this.type}`);
    }

    if (this.previewContent) {
      this.element.classList.add('artifact--has-preview');
    }

    this.element.dataset.state = this.state;

    this._updateContent();
    this._attachEventHandlers();

    return this.element;
  }

  /**
   * Update the artifact content
   * @private
   */
  _updateContent() {
    if (!this.element) return;

    // Build actions HTML (button, standalone chevron, checkmark, or nothing)
    let actionsHtml = '';
    if (this.showButton) {
      actionsHtml = `
        <div class="artifact__actions">
          <button class="artifact__button" type="button">
            <span class="artifact__button-text">${escapeHtml(this.buttonText)}</span>
            <svg class="artifact__button-chevron" width="14" height="14" viewBox="0 0 16 16">
              <use href="#icon-chevron-right"/>
            </svg>
          </button>
        </div>`;
    } else if (this.showCheckmark) {
      actionsHtml = `
        <div class="artifact__actions artifact__actions--checkmark">
          <svg class="artifact__checkmark" width="17" height="17" viewBox="0 0 20 20">
            <use href="#icon-checkmark"/>
          </svg>
        </div>`;
    } else if (this.showChevron) {
      const chevronTextHtml = this.chevronText
        ? `<span class="artifact__chevron-text">${escapeHtml(this.chevronText)}</span>`
        : '';
      actionsHtml = `
        <div class="artifact__actions artifact__actions--chevron-only">
          ${chevronTextHtml}
          <svg class="artifact__chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>`;
    }

    // Build icon area (only if showIcon is true)
    let iconHtml = '';
    if (this.showIcon) {
      const iconContent = this._buildIconContent();
      iconHtml = `
          <div class="artifact__icon-wrapper">
            <div class="artifact__icon">
              ${iconContent}
            </div>
          </div>`;
    }

    // Build NEW tag HTML if needed
    const newTagHtml = this.showNewTag
      ? '<span class="artifact__new-tag">NEW</span>'
      : '';

    // Build description HTML if present
    const descriptionHtml = this.description
      ? `<div class="artifact__description">${escapeHtml(this.description)}</div>`
      : '';

    // Build subtitle HTML if present
    const subtitleHtml = this.subtitle
      ? `<div class="artifact__subtitle">${escapeHtml(this.subtitle)}</div>`
      : '';

    this.element.innerHTML = `
      <div class="artifact__inner">
        <div class="artifact__left">
          ${iconHtml}
          <div class="artifact__content">
            <div class="artifact__title-row">
              <span class="artifact__title">${escapeHtml(this.title)}</span>
              ${newTagHtml}
            </div>
            ${descriptionHtml}
            ${subtitleHtml}
          </div>
        </div>
        ${actionsHtml}
      </div>
    `;

    // Update classes
    this.element.dataset.state = this.state;
    this.element.classList.toggle('artifact--clickable', this.clickable);
    this.element.classList.toggle('artifact--has-preview', !!this.previewContent);
    this.element.classList.toggle('artifact--no-icon', !this.showIcon);
  }

  /**
   * Build the icon or preview content for the artifact
   * @private
   * @returns {string} HTML string for icon or preview content
   */
  _buildIconContent() {
    // Preview content takes priority (for insights)
    if (this.previewContent) {
      const previewTitle = this.previewContent.title || '';
      const previewBody = this.previewContent.body || '';
      return `
        <div class="artifact__preview">
          <h4 class="artifact__preview-title">${escapeHtml(previewTitle)}</h4>
          <p class="artifact__preview-body">${escapeHtml(previewBody)}</p>
        </div>`;
    }

    // Default: SVG icon
    return `<svg width="20" height="20"><use href="#${this.iconId}"/></svg>`;
  }

  /**
   * Handle button click event
   * @param {Event} e - Click event
   * @private
   */
  _handleButtonClick(e) {
    e.stopPropagation(); // Prevent artifact click from also firing
    if (this.onButtonClick) {
      this.onButtonClick(e);
    } else if (this.onArtifactClick) {
      // Fall back to artifact click handler if no button handler
      this.onArtifactClick(e);
    }
  }

  /**
   * Attach button click handler with proper cleanup
   * @private
   */
  _attachButtonHandler() {
    // Remove handler from previous button if exists
    if (this._currentButton) {
      this._currentButton.removeEventListener('click', this._boundButtonClickHandler);
    }

    // Attach to new button
    this._currentButton = this.element?.querySelector('.artifact__button');
    if (this._currentButton) {
      this._currentButton.addEventListener('click', this._boundButtonClickHandler);
    }
  }

  /**
   * Attach event handlers
   * @private
   */
  _attachEventHandlers() {
    if (!this.element) return;

    // Artifact body click (excludes button)
    this.element.addEventListener('click', (e) => {
      // Don't trigger if clicking the button (button has its own handler)
      if (e.target.closest('.artifact__button')) return;

      if (this.clickable && this.onArtifactClick) {
        this.onArtifactClick(e);
      }
    });

    // Keyboard support for accessibility
    this.element.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && this.clickable && this.onArtifactClick) {
        e.preventDefault();
        this.onArtifactClick(e);
      }
    });

    // Button click
    this._attachButtonHandler();
  }

  /**
   * Update artifact configuration
   * @param {Object} updates - Properties to update
   * @param {Object} [options]
   * @param {boolean} [options.animateHeight=false] - Ease the card between its old
   *   and new height instead of snapping. For state swaps that add or drop a line
   *   (questionnaire → completed drops the description), where an instant reflow
   *   hops every message below the card.
   */
  update(updates, options = {}) {
    if (updates.iconId !== undefined) this.iconId = updates.iconId;
    if (updates.title !== undefined) this.title = updates.title;
    if (updates.description !== undefined) this.description = updates.description;
    if (updates.subtitle !== undefined) this.subtitle = updates.subtitle;
    if (updates.buttonText !== undefined) this.buttonText = updates.buttonText;
    if (updates.showButton !== undefined) this.showButton = updates.showButton;
    if (updates.showChevron !== undefined) this.showChevron = updates.showChevron;
    if (updates.chevronText !== undefined) this.chevronText = updates.chevronText;
    if (updates.showCheckmark !== undefined) this.showCheckmark = updates.showCheckmark;
    if (updates.showNewTag !== undefined) this.showNewTag = updates.showNewTag;
    if (updates.showIcon !== undefined) this.showIcon = updates.showIcon;
    if (updates.state !== undefined) this.state = updates.state;
    if (updates.clickable !== undefined) this.clickable = updates.clickable;
    if (updates.previewContent !== undefined) this.previewContent = updates.previewContent;

    if (options.animateHeight) {
      this._updateContentWithResize();
    } else {
      this._updateContent();
    }
    this._attachButtonHandler();
  }

  /**
   * Re-render the card, easing its box between the old and new height.
   *
   * The content swap itself is instant — only the card's height is animated, so
   * the transcript below it slides rather than hops.
   * @private
   */
  _updateContentWithResize() {
    const element = this.element;
    if (!element || !element.isConnected) {
      this._updateContent();
      return;
    }

    const startHeight = element.offsetHeight;
    this._updateContent();
    const endHeight = element.offsetHeight;

    const reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || startHeight === endHeight || !startHeight || !endHeight) return;

    element.style.height = `${startHeight}px`;
    element.style.overflow = 'hidden';
    void element.offsetHeight; // commit the start height before transitioning off it

    const settle = () => {
      clearTimeout(safety);
      element.removeEventListener('transitionend', onTransitionEnd);
      element.style.height = '';
      element.style.overflow = '';
      element.style.transition = '';
    };
    const onTransitionEnd = (event) => {
      if (event.target !== element || event.propertyName !== 'height') return;
      settle();
    };

    element.addEventListener('transitionend', onTransitionEnd);
    const safety = setTimeout(settle, RESIZE_DURATION_MS + 50);

    element.style.transition = `height ${RESIZE_DURATION_MS}ms ease-out`;
    element.style.height = `${endHeight}px`;
  }

  /**
   * Set artifact state
   * @param {string} state - New state value
   */
  setState(state) {
    this.state = state;
    if (this.element) {
      this.element.dataset.state = state;
    }
  }

  /**
   * Set clickable state
   * @param {boolean} clickable - Whether artifact should be clickable
   */
  setClickable(clickable) {
    this.clickable = clickable;
    if (this.element) {
      this.element.classList.toggle('artifact--clickable', clickable);
    }
  }

  /**
   * Get the DOM element
   * @returns {HTMLElement|null}
   */
  getElement() {
    return this.element;
  }

  /**
   * Destroy the artifact and clean up
   */
  destroy() {
    // Clean up button handler
    if (this._currentButton) {
      this._currentButton.removeEventListener('click', this._boundButtonClickHandler);
      this._currentButton = null;
    }

    if (this.element) {
      this.element.remove();
      this.element = null;
    }
  }
}
